import * as React from "react";

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  cn,
} from "@alight/ui";
import { MoreHorizontal, type LucideIcon } from "lucide-react";

/**
 * Per-row actions, collapsed into one menu.
 *
 * These tables used to lay every action out as a labelled button in the last
 * column. That was right about the WORDS — an icon-only row made the host click
 * a button to find out what it did, and one of them destroys a record — but four
 * or five buttons wrapped onto three lines, so a single child took up as much
 * height as a paragraph and the list stopped being scannable.
 *
 * The menu keeps the words (and gains room for a plain-language note on the
 * consequence of each one) while the row itself is a single line again.
 */
export function RowActions({
  label,
  children,
}: {
  /** Named for the row's subject, e.g. "Actions for Sam Chen". */
  label: string;
  children: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={label}>
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * One action in the menu: an icon, the word for what it does, and a line saying
 * what happens if you pick it — who gets emailed, what cannot be undone.
 */
export function RowAction({
  icon: Icon,
  label,
  description,
  destructive,
  disabled,
  onSelect,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      // Radix hands focus back to the trigger on close; letting it do that
      // before a dialog opens keeps the two from fighting over focus.
      onSelect={() => window.setTimeout(onSelect, 0)}
      className={cn(
        "items-start gap-2.5 py-2",
        destructive && "text-error focus:bg-error/10 focus:text-error",
      )}
    >
      <Icon className="mt-0.5 h-4 w-4" aria-hidden />
      <span className="flex flex-col gap-0.5">
        <span className="font-medium leading-none">{label}</span>
        <span
          className={cn(
            "text-xs font-normal leading-snug",
            destructive ? "text-error/80" : "text-muted",
          )}
        >
          {description}
        </span>
      </span>
    </DropdownMenuItem>
  );
}
